import { ToastContainer } from '@/components/ToastContainer';
import { AuthProvider } from '@/features/authentication/contexts';
import { appRoutes } from '@/routes/appRoutes';
import { subdomainRoutes } from '@/routes/subdomainRoutes';
import {
  APP_SUBDOMAIN,
  buildMarketingUrl,
  getSubdomain,
} from '@/utils/subdomainUtils';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { PrimeReactProvider } from 'primereact/api';
import type { FC } from 'react';
import { useEffect } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  type RouteObject,
} from 'react-router-dom';

// Register data label plugins
ChartJS.register(ChartDataLabels);

/**
 * Component that redirects to marketing site
 * Used when accessing the base domain without a subdomain
 */
export const MarketingRedirect: FC = () => {
  useEffect(() => {
    window.location.href = buildMarketingUrl();
  }, []);

  return null; // Don't render anything while redirecting
};

/**
 * Creates the appropriate router based on subdomain
 * - App subdomain (app.hostname.com) → Admin interface routes
 * - Organization subdomain (org.hostname.com) → Public survey taking routes
 * - Base domain (hostname.com) → Redirect to marketing site
 */
export function createAppRouter(): ReturnType<typeof createBrowserRouter> {
  const subdomain = getSubdomain();

  // Organization subdomain - public survey taking routes
  if (subdomain && subdomain !== APP_SUBDOMAIN) {
    return createBrowserRouter(subdomainRoutes);
  }

  // App subdomain - authenticated admin interface
  if (subdomain === APP_SUBDOMAIN) {
    return createBrowserRouter(appRoutes);
  }

  // Base domain - redirect to marketing site
  const marketingRoute: RouteObject[] = [
    {
      path: '*',
      element: <MarketingRedirect />,
    },
  ];

  return createBrowserRouter(marketingRoute);
}

// Create the router outside the component to avoid recreating on every render
const router = createAppRouter();

const App: FC = () => {
  return (
    <PrimeReactProvider>
      <ToastContainer />
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </PrimeReactProvider>
  );
};

export default App;
